import "../css/EscapeBooth.css";

import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import NavBar from "../webcomponents/NavBar";

// The magic mirror at the end of the Slime Magic Academy escape room: the QR code on the printed strip opens
// /escape?id=<visit id>. The booth writes one document per visit into the "escapebooth" collection
// (videoLink, photoLink, printLink) once its upload finishes - a fresh print can be a minute early.
// Look (Panu, 1 Oct 2026): the escape room's own night-purple glass, the designer's title ribbons, the video inside the
// wooden mirror, the portrait behind the print's gold window, ONE strip of the print; no pass code on the page.
const A = "/assets/escape/";
const BOOK = "https://lin.ee/Uoj6YqH";

const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v3h16v-3" />
    </svg>
);
const ShareIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </svg>
);

const Stars = () => (
    <div className="esc-stars" aria-hidden="true">
        {[[6, 120, 0], [88, 160, -1], [14, 520, -2], [92, 700, -0.5], [4, 1100, -1.6], [86, 1500, -2.4], [10, 1900, 0], [90, 2300, -1.2]].map(([x, y, d], i) => (
            <i key={i} style={{ left: x + "%", top: y + "px", animationDelay: d + "s" }} />
        ))}
    </div>
);

const Shell = ({ children }) => (
    <div className="esc">
        <div className="esc-shine" />
        <Stars />
        <NavBar />
        <div className="esc-wrap">
            <img className="esc-title" src={A + "title.png"} alt="Slime Magic Academy · Escape Room · The Missing Moon Liquid?" />
            <div className="esc-props"><img className="hat" src={A + "prop_hat.png"} alt="" /><img className="cat" src={A + "prop_cat.png"} alt="" /></div>
            {children}
            <div className="esc-book" onClick={() => window.open(BOOK, "_blank", "noopener,noreferrer")}>BOOK SESSION</div>
            <div className="esc-foot"><img src="/assets/homepage/slimeville_logo_footer.png" alt="Slimeville" /></div>
        </div>
    </div>
);

/** The print is two strips side by side (1200 x 1800): this makes a picture of the LEFT strip only. Null if the browser cannot. */
async function cropToOneStrip(printUrl) {
    try {
        const response = await fetch(printUrl);
        const blob = await response.blob();
        const bitmap = await createImageBitmap(blob);
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(bitmap.width / 2);
        canvas.height = bitmap.height;
        canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
        return await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
    } catch (err) {
        console.warn("could not cut the print to one strip:", err);
        return null;
    }
}

function saveBlob(blob, fileName) {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
}

const EscapeBoothPage = () => {
    const [visitId, setVisitId] = useState("");
    const [visit, setVisit] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [tries, setTries] = useState(0);
    const [strip, setStrip] = useState(null);          // { blob, url } of the single strip
    const [busy, setBusy] = useState("");

    const location = useLocation();
    const canShare = typeof navigator !== "undefined" && !!navigator.share;

    useEffect(() => {
        const id = new URLSearchParams(location.search).get("id");
        if (id) setVisitId(id);
        else setIsLoading(false);
    }, [location]);

    useEffect(() => {
        if (visitId === "") return;
        let cancelled = false;
        const load = async () => {
            const snap = await getDoc(doc(db, "escapebooth", visitId));
            if (cancelled) return;
            if (snap.exists()) {
                setVisit(snap.data());
                setIsLoading(false);
            } else if (tries < 12) {
                // the booth may still be uploading (the print comes out before the upload is done): look again
                setTimeout(() => setTries(t => t + 1), 5000);
            } else {
                setIsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [visitId, tries]);

    // one strip of the print, cut in the browser
    useEffect(() => {
        if (!visit || !visit.printLink) return;
        let url = null;
        cropToOneStrip(visit.printLink).then(blob => {
            if (!blob) return;
            url = URL.createObjectURL(blob);
            setStrip({ blob, url });
        });
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [visit]);

    const fetchBlob = async (url) => (await fetch(url)).blob();

    const download = useCallback(async (key, url, fileName, readyBlob) => {
        try {
            setBusy(key);
            saveBlob(readyBlob || await fetchBlob(url), fileName);
        } catch (err) {
            console.error("Download failed:", err);
            window.open(url, "_blank", "noopener,noreferrer");     // the browser can still save it from a new tab
        } finally {
            setBusy("");
        }
    }, []);

    const share = useCallback(async (key, url, fileName, type, readyBlob) => {
        try {
            setBusy(key);
            const blob = readyBlob || await fetchBlob(url);
            const file = new File([blob], fileName, { type });
            const data = { files: [file], title: "Slime Magic Academy · Escape Room" };
            if (navigator.canShare && !navigator.canShare(data)) throw new Error("files cannot be shared here");
            await navigator.share(data);
        } catch (err) {
            if (err && err.name !== "AbortError") {
                console.warn("Share failed, sharing the page link instead:", err);
                try { await navigator.share({ title: "Slime Magic Academy · Escape Room", url: window.location.href }); } catch (e) { /* closed */ }
            }
        } finally {
            setBusy("");
        }
    }, []);

    const files = visit ? [
        visit.videoLink && { key: "video", url: visit.videoLink, name: visit.id + "_slimeville_spell.mp4", type: "video/mp4" },
        visit.photoLink && { key: "photo", url: visit.photoLink, name: visit.id + "_slimeville_portrait.png", type: "image/png" },
        visit.printLink && { key: "print", url: visit.printLink, name: visit.id + "_slimeville_strip.png", type: "image/png", blob: strip ? strip.blob : null },
    ].filter(Boolean) : [];

    const saveEverything = async () => {
        setBusy("all");
        for (const f of files) {
            try { saveBlob(f.blob || await fetchBlob(f.url), f.name); } catch (err) { console.error("Download failed:", err); }
            await new Promise(r => setTimeout(r, 700));             // browsers drop downloads fired in the same instant
        }
        setBusy("");
    };

    if (isLoading) {
        return (
            <Shell>
                <div className="esc-plate">
                    {tries > 0 ? "THE MIRROR IS STILL SENDING YOUR PORTRAIT…" : "LOOKING INTO THE MIRROR…"}
                    <small>{tries > 0 ? "It can take a minute after the print comes out. This page keeps looking." : "One moment, young wizard."}</small>
                </div>
            </Shell>
        );
    }

    if (!visit) {
        return (
            <Shell>
                <div className="esc-plate">
                    {visitId ? "YOUR PORTRAIT IS NOT HERE YET" : "NO PORTRAIT FOR THIS LINK"}
                    <small>{visitId ? "Give it a minute and scan the QR code on your strip again." : "Scan the QR code on your printed strip."}</small>
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <div className="esc-hello">The mirror remembers you, young wizard.<br />Your spell, your portrait and your print — keep them forever.</div>

            {visit.videoLink && (
                <section className="esc-card">
                    <h2 className="esc-label"><span>KEEPSAKE 1</span>Your Spell</h2>
                    <div className="esc-mirror">
                        <video src={visit.videoLink} autoPlay muted loop playsInline controls />
                        <img className="frame" src={A + "mirror_frame.png"} alt="" />
                    </div>
                    <div className="esc-btns">
                        <button className="esc-btn" disabled={busy !== ""} onClick={() => download("video", visit.videoLink, files[0].name)}><DownloadIcon />SAVE VIDEO</button>
                        {canShare && <button className="esc-btn ghost" disabled={busy !== ""} onClick={() => share("video", visit.videoLink, files[0].name, "video/mp4")}><ShareIcon />SHARE</button>}
                    </div>
                </section>
            )}

            {visit.photoLink && (
                <section className="esc-card">
                    <h2 className="esc-label"><span>KEEPSAKE {visit.videoLink ? 2 : 1}</span>Your Portrait</h2>
                    <div className="esc-paper">
                        <div className="esc-window">
                            <img className="pic" src={visit.photoLink} alt="your portrait in the magic mirror" />
                            <img className="edge" src={A + "window_edge.png"} alt="" />
                        </div>
                    </div>
                    <div className="esc-btns">
                        <button className="esc-btn" disabled={busy !== ""} onClick={() => download("photo", visit.photoLink, visit.id + "_slimeville_portrait.png")}><DownloadIcon />SAVE PHOTO</button>
                        {canShare && <button className="esc-btn ghost" disabled={busy !== ""} onClick={() => share("photo", visit.photoLink, visit.id + "_slimeville_portrait.png", "image/png")}><ShareIcon />SHARE</button>}
                    </div>
                </section>
            )}

            {visit.printLink && (
                <section className="esc-card">
                    <h2 className="esc-label"><span>KEEPSAKE {files.length}</span>Your Print</h2>
                    <div className="esc-stripbox">
                        {strip
                            ? <img className="esc-strip" src={strip.url} alt="your printed strip" />
                            : <div className="esc-strip-half"><img src={visit.printLink} alt="your printed strip" /></div>}
                        <img className="esc-potion" src={A + "potion.png"} alt="" />
                    </div>
                    <div className="esc-btns">
                        <button className="esc-btn" disabled={busy !== ""} onClick={() => download("print", visit.printLink, visit.id + "_slimeville_strip.png", strip && strip.blob)}><DownloadIcon />SAVE PRINT</button>
                        {canShare && <button className="esc-btn ghost" disabled={busy !== ""} onClick={() => share("print", visit.printLink, visit.id + "_slimeville_strip.png", "image/png", strip && strip.blob)}><ShareIcon />SHARE</button>}
                    </div>
                </section>
            )}

            {files.length > 1 && (
                <div className="esc-all">
                    <button className="esc-btn big" disabled={busy !== ""} onClick={saveEverything}>✦ SAVE EVERYTHING ✦</button>
                    <div className="esc-tip">On a phone, saved files land in Photos / Downloads. Press and hold a picture to share it straight to LINE.</div>
                </div>
            )}
        </Shell>
    );
};

export default EscapeBoothPage;
